import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "../utils/axios";
import { showToast } from "../components/ui/toast";
import { API_ROUTES } from "./routes";

export const useCommsOrganizationsQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.COMMS_LOG_ORGANIZATIONS],
    queryFn: () => api.get(API_ROUTES.COMMS_LOG_ORGANIZATIONS),
    staleTime: 1000 * 60 * 10,
  });

  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useCommsMessagesQuery = (filters) => {
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [API_ROUTES.COMMS_LOG_MESSAGES, filters],
    queryFn: () => api.get(API_ROUTES.COMMS_LOG_MESSAGES, { params: filters }),
    enabled: Boolean(filters.organizationId),
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.data?.data ?? [],
    pagination: data?.data?.pagination,
    isPending,
    isFetching,
    isError,
    error,
  };
};

export const useCommsPropertiesQuery = (filters) => {
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [API_ROUTES.COMMS_LOG_PROPERTIES, filters],
    queryFn: () => api.get(API_ROUTES.COMMS_LOG_PROPERTIES, { params: filters }),
    enabled: Boolean(filters.organizationId),
    placeholderData: keepPreviousData,
  });

  return {
    data: data?.data?.data ?? [],
    pagination: data?.data?.pagination,
    isPending,
    isFetching,
    isError,
    error,
  };
};

export const useCommsPropertyChatQuery = (propertyId, page = 1) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.COMMS_LOG_CHAT, propertyId, page],
    queryFn: () =>
      api.get(`${API_ROUTES.COMMS_LOG_CHAT}/${propertyId}`, {
        params: { page, limit: 50 },
      }),
    enabled: Boolean(propertyId),
  });

  return {
    chat: data?.data?.data?.chat ?? null,
    messages: data?.data?.data?.messages ?? [],
    pagination: data?.data?.pagination,
    isPending,
    isError,
    error,
  };
};

// Stop, resend and retract all move the same row, so they invalidate the same lists.
const useCommsMessageAction = (route, fallbackError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => api.post(route, payload),
    onSuccess: (response) => {
      const { success, message } = response?.data || {};
      showToast(success ? "success" : "error", message || fallbackError);
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.COMMS_LOG_MESSAGES] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.COMMS_LOG_PROPERTIES] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.COMMS_LOG_CHAT] });
    },
    onError: (error) => {
      showToast(
        "error",
        error?.response?.data?.message || fallbackError,
      );
    },
  });
};

export const useStopCommsMessage = () =>
  useCommsMessageAction(API_ROUTES.COMMS_LOG_STOP, "Could not stop the message.");

export const useSaveCommsMessage = () =>
  useCommsMessageAction(API_ROUTES.COMMS_LOG_SAVE, "Could not save the message.");

export const useSendVendorGroupMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_VENDOR_GROUP,
    "Could not send the message to the vendor group.",
  );

export const useApproveCommsMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_APPROVE,
    "Could not send the message to the agent.",
  );

export const useRecallCommsMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_RECALL,
    "Could not take the message back.",
  );

export const useResendCommsMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_RESEND,
    "Could not resend the message.",
  );

export const useRetractCommsMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_RETRACT,
    "Could not retract the message.",
  );

// Scoped to a property, so the picker lists facts about this campaign, not 60 names.
export const useCommsTemplatesQuery = (propertyId) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.COMMS_LOG_TEMPLATES, propertyId],
    queryFn: () =>
      api.get(API_ROUTES.COMMS_LOG_TEMPLATES, { params: { propertyId } }),
    enabled: Boolean(propertyId),
    staleTime: 1000 * 60 * 5,
  });

  return {
    templates: data?.data?.data?.templates ?? [],
    unsent: data?.data?.data?.unsent ?? {
      waitingOnBeleef: [],
      waitingOnAgent: [],
      orphaned: [],
    },
    isPending,
    isError,
    error,
  };
};

// Nothing is stored until it is sent.
export const usePreviewCommsMessage = () =>
  useMutation({
    mutationFn: (payload) => api.post(API_ROUTES.COMMS_LOG_PREVIEW, payload),
    onError: (error) =>
      showToast(
        "error",
        error?.response?.data?.message || "Could not run the generator.",
      ),
  });

export const useManualSendCommsMessage = () =>
  useCommsMessageAction(
    API_ROUTES.COMMS_LOG_MANUAL_SEND,
    "Could not send the message.",
  );
